import { SpreadBackgroundOptionsLayer } from '@/components/app/backgrounds/spread-background-options'
import { DoubleSpreadGutterTintAboveShapes } from '@/components/app/document/double-spread-gutter-tint-above-shapes'
import { DoubleSpreadSpineBarAboveShapes } from '@/components/app/document/double-spread-spine-bar-above-shapes'
import { TextLinkingOverlay } from '@/components/app/editor/text-linking/text-linking-overlay'
import { VisualLayer } from '@/components/app/editor/visual-layer/visual-layer'
import { EMPTY_TEXT_CHAINS } from '../constants/empty-text-chains'
import type {
  CanvasEditModes,
  CanvasFoundation,
  CanvasPointerGestures,
  CanvasTextLinking,
  CanvasToolModes,
  CanvasViewport,
} from '../types/canvas-groups'
import type { ResolvedCenterCanvasConfig } from '../types/resolved-center-canvas-config'

interface ScreenSpaceLayersProps {
  config: Pick<
    ResolvedCenterCanvasConfig,
    | 'isTextToolActive'
    | 'linkedTextChains'
    | 'showTextLinking'
    | 'bleedWidth'
    | 'bleedHeight'
    | 'bleedMargin'
    | 'trimWidth'
    | 'trimHeight'
    | 'centerGap'
    | 'showBleed'
  >
  foundation: Pick<
    CanvasFoundation,
    'internalShapeManagerRef' | 'effectiveSmartGuides' | 'activeSmartGuides'
  >
  editModes: Pick<
    CanvasEditModes,
    'isNodeEditMode' | 'isCropMode' | 'isTextOnPathMode' | 'selectedShapeIdsForUnified'
  >
  pointer: Pick<CanvasPointerGestures, 'marqueeRect' | 'textDragPreviewRect' | 'marqueeZoomRect'>
  viewport: Pick<
    CanvasViewport,
    | 'scale'
    | 'singlePageHorizontalGuideSpan'
    | 'spreadBackgroundGeometry'
    | 'isSinglePage'
    | 'snappedSpreadOriginCanvasX'
  >
  linking: Pick<
    CanvasTextLinking,
    | 'textLinkingRevision'
    | 'textLinking'
    | 'textToolHoverFrameId'
    | 'handleTextOutPortClick'
    | 'handleTextInPortClick'
    | 'handleTextUnlinkClick'
    | 'handleTextPortHoverChange'
    | 'cancelTextLinking'
  >
  toolModes: CanvasToolModes
}

/**
 * The layers that draw in screen space — siblings of the document's `scale()` wrapper that
 * compute final screen pixels themselves: the visual layer (selection chrome, drag rects, smart
 * guides), the background tint/remove toolbars, the text-linking overlay, and the bindery spine
 * bar and gutter tint.
 */
export function ScreenSpaceLayers({
  config,
  foundation,
  editModes,
  pointer,
  viewport,
  linking,
  toolModes,
}: ScreenSpaceLayersProps) {
  const {
    isTextToolActive,
    linkedTextChains,
    showTextLinking,
    bleedWidth,
    bleedHeight,
    bleedMargin,
    trimWidth,
    trimHeight,
    centerGap,
    showBleed,
  } = config
  const { internalShapeManagerRef, effectiveSmartGuides, activeSmartGuides } = foundation
  const { isNodeEditMode, isCropMode, isTextOnPathMode, selectedShapeIdsForUnified } = editModes
  const { marqueeRect, textDragPreviewRect, marqueeZoomRect } = pointer
  const {
    scale,
    singlePageHorizontalGuideSpan,
    spreadBackgroundGeometry,
    isSinglePage,
    snappedSpreadOriginCanvasX,
  } = viewport
  const {
    textLinkingRevision,
    textLinking,
    textToolHoverFrameId,
    handleTextOutPortClick: onOutPortClick,
    handleTextInPortClick: onInPortClick,
    handleTextUnlinkClick: onUnlinkClick,
    handleTextPortHoverChange: onPortHoverChange,
    cancelTextLinking: onCancelLinking,
  } = linking
  const { isInShapeCreationMode, isInPenToolMode, isInTextCreationMode } = toolModes

  return (
    <>
      {/* Visual layer — deliberately a sibling of the `scale()` wrapper, not a child of it.
          Everything inside that wrapper is drawn in document units and then scaled, which
          is why chrome in there needs inverse-scale/non-scaling-stroke tricks to hold a
          constant screen size. This layer instead receives `scale` and computes final
          screen pixels itself, so a 1px line is exactly 1px at every zoom with nothing to
          compensate. The wrapper is `origin-top-left` with no offset of its own, so the
          mapping is simply `screen = doc * scale` from this element's own top-left. */}
      <div
        className="absolute inset-0"
        style={{ pointerEvents: 'none', overflow: 'visible', zIndex: 46 }}
        data-visual-layer=""
      >
        <VisualLayer
          shapeManagerRef={internalShapeManagerRef}
          scale={scale}
          // Node edit, crop mode, and the text tool each own the selection surface
          // themselves; the migrated chrome stays out of their way for now.
          suppressChrome={isNodeEditMode || isCropMode || isTextOnPathMode || isTextToolActive}
          dragRects={[
            { variant: 'marqueeSelect', rect: marqueeRect },
            { variant: 'createPreview', rect: textDragPreviewRect },
            { variant: 'marqueeZoom', rect: marqueeZoomRect },
          ]}
          smartGuides={effectiveSmartGuides ? activeSmartGuides : undefined}
          smartGuidesHorizontalClipSpan={singlePageHorizontalGuideSpan}
        />
      </div>
      {/* Background tint/remove toolbars — screen-space for the same reason as the Visual
          layer above (constant on-screen size at every zoom), and above every other canvas
          overlay since it's interactive. Renders nothing unless the Backgrounds panel is
          open and the spread has a background. */}
      <div
        className="absolute inset-0"
        style={{ pointerEvents: 'none', overflow: 'visible', zIndex: 50 }}
      >
        <SpreadBackgroundOptionsLayer scale={scale} {...spreadBackgroundGeometry} />
      </div>
      {/* Text Linking overlay (WMPCM-6852) — connectors, numbered ports, linking-mode
          chrome. A positioned sibling of the Visual layer above, not a child of the
          scale() wrapper it used to live in: z-index only orders elements within their own
          stacking context, and `transform: scale()` on that wrapper (and every element
          inside it) makes it one, so no zIndex prop in there could ever outrank the Visual
          layer's zIndex 46 — the ports painted first no matter how high that prop went,
          which is what put resize/rotate handles on top of them.

          This wrapper carries no transform of its own — `TextLinkingLayer` now computes
          final screen pixels itself (`doc * scale`, via `chromeFrameOf` in
          `text-linking/helpers/chrome-frame-of.ts`), the same
          technique the Visual layer's own children and the spine bar below use, and for
          the same reason: no CSS transform on this element or its wrapper means no
          secondary scaling step for the rasterizer to disagree with (see the spine bar's
          comment for the concrete symptom this class of bug produces at high zoom), and
          every port/glyph size is a literal on-screen pixel rather than a doc-px value
          counter-scaled back down. It still needs its own zIndex, competing with the
          Visual layer's 46 in the SAME stacking context, which is the part this wrapper's
          history is actually about. Frame population is shape-only (Phase 2: TextManager
          never renders — see WMPCM-7988). Frames and chains are no longer resolved here at
          all: the overlay samples them itself on an animation frame, the same way the
          Visual layer samples the geometry it draws handles against. Doing it inline on
          CenterCanvas's render only worked while something re-rendered CenterCanvas,
          and `textLinkingRevision` is bumped from two commit-path call sites — so undo/
          redo and remote multiplayer edits moved a frame, moved its handles, and left its
          ports behind (WMPCM-9199). */}
      <div className="absolute inset-0" style={{ pointerEvents: 'none', zIndex: 47 }}>
        <TextLinkingOverlay
          zIndex={0}
          revision={textLinkingRevision}
          shapeManagerRef={internalShapeManagerRef}
          linkedTextChains={linkedTextChains ?? EMPTY_TEXT_CHAINS}
          // A converted shape frame is selected via ShapeManager, so its ports must key
          // off shape selection.
          selectedTextIds={selectedShapeIdsForUnified}
          showTextLinking={showTextLinking}
          scale={scale}
          linking={textLinking}
          selectToolActive={!isInShapeCreationMode && !isInPenToolMode && !isTextToolActive}
          textToolActive={isInTextCreationMode}
          textToolHoverFrameId={textToolHoverFrameId}
          onOutPortClick={onOutPortClick}
          onInPortClick={onInPortClick}
          onUnlinkClick={onUnlinkClick}
          onPortHoverChange={onPortHoverChange}
          onCancelLinking={onCancelLinking}
        />
      </div>
      {/* Solid bindery spine bar. Same parent as the Visual layer above (this "Center
          spread" wrapper's own offset already puts that origin at canvas doc-space (0,0)
          on screen), and like the Text Linking overlay right above, this computes final
          screen pixels itself (`doc * scale`) rather than sitting inside a `transform:
          scale()` wrapper — the same technique `VisualLayer`'s own children use, and for
          the same reason:
          confirmed live via devtools that a `transform: scale()` ancestor (first
          `documentCanvasRef`'s own tree, then a dedicated re-applied one matching Text
          Linking's pattern) can rasterize this element's pixels at a position that doesn't
          match its own computed CSS box, however exactly that box is centered on the seam.
          No CSS transform is applied to this element or its wrapper, so there is no
          secondary scaling step left for the rasterizer to disagree with. */}
      {!isSinglePage && (
        <div
          className="absolute inset-0"
          style={{ pointerEvents: 'none', overflow: 'visible', zIndex: 48 }}
        >
          <DoubleSpreadSpineBarAboveShapes
            bleedMargin={bleedMargin}
            trimHeight={trimHeight}
            spineSnapCanvasX={snappedSpreadOriginCanvasX}
            scale={scale}
          />
        </div>
      )}
      {/* Bindery gutter tint column — same fix, same reason, as the spine bar above: its
          computed box was centered on the shared seam, but the painted band still rendered
          off-center at high zoom from inside `documentCanvasRef`'s `transform: scale()`
          tree. Note the trade-off this move accepts: `documentCanvasRef`'s scaled subtree
          (shapes, dashed trim, margin guides) has no explicit z-index of its own, so it is
          capped as one group below every explicit-z-index sibling here — meaning this tint
          (like the spine bar) now always renders above that whole group, not just above
          shapes as `PrintBleedTints` originally ordered it (tint below margin guides below
          dashed trim, all within that shared group). The dashed trim / margin lines will
          therefore render lightly tinted rather than perfectly crisp where they cross this
          ~`centerGap`-wide column — accepted as materially less noticeable than the
          reported off-center gutter, but flagged here rather than silently traded away. */}
      {!isSinglePage && (
        <div
          className="absolute inset-0"
          style={{ pointerEvents: 'none', overflow: 'visible', zIndex: 10 }}
        >
          <DoubleSpreadGutterTintAboveShapes
            bleedWidth={bleedWidth}
            bleedMargin={bleedMargin}
            bleedHeight={bleedHeight}
            trimWidth={trimWidth}
            trimHeight={trimHeight}
            centerGap={centerGap}
            spineSnapCanvasX={snappedSpreadOriginCanvasX}
            scale={scale}
            showOutsideTrim={showBleed}
          />
        </div>
      )}
    </>
  )
}
